import { Accordion, Box, Flex, Radio, Space, Stack, Text } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { ReactNode, useState } from 'react';
import { useIsAfternoon } from 'src/common/useIsAfternoonHook';
import { TableHeader } from '../UI/common';
import EventsSvg from './Events.svg?react';
import FirstGraphSvg from './FirstGraph.svg?react';
import useExpenditures from './hooks/useExpenditures';
import useIncomes from './hooks/useIncomes';
import useLifeEvents from './hooks/useLifeEvents';
import NotesSvg from './Notes.svg?react';
import SecondGraphSvg from './SecondGraph.svg?react';
import PageLoader from 'src/UI/loader/PageLoader';
import { Period, totalForPeriod } from './utils';
import { formatMoney } from 'src/common/currency';
import { IncomePanel } from './form/IncomePanel';
import { ExpenditurePanel } from './form/ExpenditurePanel';
import { LifeEventPanel } from './form/LifeEventPanel';
import { useCurrency } from 'src/common/hooks/useCurrency';
import { LeftOutNote } from './components/LeftOutNote';
import {
  afterTaxLeftOutReason,
  combineLeftOutNames,
  expenditureLeftOutReason,
  expenditureName,
  INCOME_TOTAL_REASON_LABELS,
  incomeLeftOutReason,
  incomeName,
  leftOutNames,
  lifeEventLeftOutReason,
  lifeEventName,
  withSectionName,
} from './leftOut';

const ACCORDION_CHEVRON = <IconChevronDown size={20} stroke={1.5} />;

export default function CashflowContentDeprecated() {
  const currency = useCurrency();
  const {
    isLoading: loadingIncomes,
    incomesData,
    upsertIncomes,
    deleteIncomes,
    period,
    setPeriod,
  } = useIncomes();

  const {
    isLoading: loadingExpenditures,
    expendituresData,
    upsertExpenditures,
    deleteExpenditures,
  } = useExpenditures();
  const { lifeEventsData, upsertLifeEvents, deleteLifeEvents } = useLifeEvents();
  const { isCrawford } = useIsAfternoon();
  const [activePanel, setActivePanel] = useState<string | null>(null);

  const incomesPreTax = totalForPeriod(incomesData, item => item.amountPreTax, period);
  const incomesPostTax = totalForPeriod(incomesData, item => item.amountPostTax, period);
  const expendituresTotal = totalForPeriod(expendituresData, item => item.amount, period);
  const incomesLeftOut = leftOutNames(incomesData, incomeLeftOutReason, incomeName);
  const expendituresLeftOut = leftOutNames(
    expendituresData,
    expenditureLeftOutReason,
    expenditureName,
  );
  const netPositionLeftOut = combineLeftOutNames(
    leftOutNames(incomesData, afterTaxLeftOutReason, withSectionName('Income', incomeName)),
    leftOutNames(
      expendituresData,
      expenditureLeftOutReason,
      withSectionName('Expenditure', expenditureName),
    ),
  );
  const lifeEventsTotal = lifeEventsData?.reduce((sum, e) => sum + (e.cost ?? 0), 0) ?? 0;
  const netPosition = incomesPostTax - expendituresTotal;
  const isNetPositive = netPosition >= 0;

  if (loadingIncomes || loadingExpenditures) {
    return <PageLoader absolute={false} />;
  }

  return (
    <Box>
      <Flex justify="flex-end" px="md" mb="xs">
        <Radio.Group value={period} onChange={value => setPeriod(value as Period)}>
          <Flex gap="xs">
            <Radio value="monthly" label="Monthly" size="xs" />
            <Radio value="annually" label="Annually" size="xs" />
          </Flex>
        </Radio.Group>
      </Flex>

      <TableHeader
        columns={[
          { label: 'Type', align: 'left' },
          { label: 'Total', align: 'right' },
        ]}
        pr="56px"
      />

      <Accordion
        defaultValue={null}
        value={activePanel}
        onChange={setActivePanel}
        variant="cells"
        chevron={ACCORDION_CHEVRON}
      >
        <Accordion.Item value="incomes" role="region">
          <Accordion.Control>
            <AccordionHeader
              title="Income"
              count={incomesData?.length ?? 0}
              amount={`${formatMoney(incomesPreTax, currency)} (${formatMoney(incomesPostTax, currency)} after tax)`}
              signPrefix="+"
              note={<LeftOutNote names={incomesLeftOut} labels={INCOME_TOTAL_REASON_LABELS} />}
            />
          </Accordion.Control>
          <Accordion.Panel>
            <IncomePanel
              items={incomesData ?? []}
              onSave={upsertIncomes}
              onDelete={deleteIncomes}
              period={period}
            />
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="expenditures" role="region">
          <Accordion.Control>
            <AccordionHeader
              title="Expenditure"
              count={expendituresData?.length ?? 0}
              amount={formatMoney(expendituresTotal, currency)}
              signPrefix="-"
              note={<LeftOutNote names={expendituresLeftOut} />}
            />
          </Accordion.Control>
          <Accordion.Panel>
            <ExpenditurePanel
              items={expendituresData ?? []}
              onSave={upsertExpenditures}
              onDelete={deleteExpenditures}
              period={period}
            />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      <Flex
        px="md"
        py="sm"
        justify="space-between"
        align="center"
        style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}
      >
        <Text size="lg" fw={700}>
          Net position
        </Text>
        <Stack gap={2} align="flex-end" pr="36px" data-testid="net-position">
          <Text
            size="lg"
            fw={700}
            c={isNetPositive ? 'green.6' : 'red.6'}
            data-testid="net-position-amount"
          >
            {isNetPositive ? '+' : '-'}
            {formatMoney(Math.abs(netPosition), currency)}
          </Text>
          <LeftOutNote names={netPositionLeftOut} />
        </Stack>
      </Flex>

      <Accordion
        value={activePanel}
        onChange={setActivePanel}
        variant="cells"
        chevron={ACCORDION_CHEVRON}
        mt="md"
      >
        <Accordion.Item value="lifeEvents" role="region">
          <Accordion.Control>
            <AccordionHeader
              title="Future life events"
              count={lifeEventsData?.length ?? 0}
              amount={formatMoney(lifeEventsTotal, currency)}
              amountLabel="Total"
              signPrefix="-"
              note={
                <LeftOutNote
                  names={leftOutNames(lifeEventsData, lifeEventLeftOutReason, lifeEventName)}
                />
              }
            />
          </Accordion.Control>
          <Accordion.Panel>
            <LifeEventPanel
              items={lifeEventsData ?? []}
              onSave={upsertLifeEvents}
              onDelete={deleteLifeEvents}
            />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      {isCrawford && (
        <Box pr="md">
          <Text size="xl" mt="xxxxl">
            Key events
          </Text>
          <Text size="sm">Last updated: 4 minutes ago</Text>
          <Space my="xxl" />
          <EventsSvg />
          <Space my="xxl" />
          <FirstGraphSvg />
          <Space my="xxl" />
          <SecondGraphSvg />
          <Space my="xxl" />
          <NotesSvg />
        </Box>
      )}
    </Box>
  );
}

type AccordionHeaderProps = {
  title: string;
  count?: number;
  amount: string;
  amountLabel?: string;
  signPrefix?: string;
  note?: ReactNode;
};

function AccordionHeader({
  title,
  count,
  amount,
  amountLabel,
  signPrefix = '',
  note,
}: AccordionHeaderProps) {
  const testId = title.toLowerCase().replace(/\s+/g, '-');

  return (
    <Flex justify="space-between">
      <Box>
        {count !== undefined && (
          <Text size="sm" data-testid={`${testId}-count`}>
            {count} {count === 1 ? 'source' : 'sources'}
          </Text>
        )}
        <Text size="lg" fw={700}>
          {title}
        </Text>
      </Box>
      <Flex direction="column" align="flex-end" justify="center">
        {amountLabel && <Text size="sm">{amountLabel}</Text>}
        <Text size="lg" fw={700} data-testid={`${testId}-amount`}>
          {signPrefix}
          {amount}
        </Text>
        {note}
      </Flex>
    </Flex>
  );
}
